import React, { useEffect, useRef, useState } from 'react';
import { IChatConversation, IChatMessage } from '../../../interfaces/chat/chat.interface';
import MessageInput from '../MessageInput';
import * as S from './styles';
import { HiDotsVertical, HiStar, HiOutlineStar, HiMail, HiUserGroup, HiArrowLeft } from 'react-icons/hi';
import chatService from '../../../services/chat/chat.service';
import { convertStringToDate } from '../../../util/common/appUtil';
import GroupParticipantsModal from '../GroupParticipantsModal';
import { useChat } from '../../../hooks/useChat';
import ButtonIcon from '../../newStudent/Buttons/ButtonIcon';

interface IChatConversationProps {
  conversation: IChatConversation;
  messages: IChatMessage[];
  onSendMessage: (content: string) => Promise<boolean>;
  onConversationUpdate?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  onBackToList?: () => void;
}

export default function ChatConversationComponent({
  conversation,
  messages,
  onSendMessage,
  onConversationUpdate,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  onBackToList,
}: IChatConversationProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesTopRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [isFavorited, setIsFavorited] = useState(conversation.isFavorited || false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const previousScrollHeight = useRef<number>(0);
  const isInitialLoad = useRef<boolean>(true);
  const previousConversationId = useRef<number | null>(null);
  const { currentUser, users } = useChat();

  useEffect(() => {
    if (conversation.id !== previousConversationId.current) {
      isInitialLoad.current = true;
      previousConversationId.current = conversation.id;
    }
  }, [conversation.id]);

  useEffect(() => {
    if (isInitialLoad.current && messages.length > 0 && messagesEndRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
        isInitialLoad.current = false;
      }, 100);
    } else if (!loadingMore && messagesEndRef.current && !isInitialLoad.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loadingMore]);

  useEffect(() => {
    if (loadingMore && messagesContainerRef.current && previousScrollHeight.current > 0) {
      const container = messagesContainerRef.current;
      const scrollDifference = container.scrollHeight - previousScrollHeight.current;
      container.scrollTop = scrollDifference;
      previousScrollHeight.current = 0;
    }
  }, [messages, loadingMore]);

  useEffect(() => {
    if (!messagesTopRef.current || !onLoadMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && hasMore) {
          if (messagesContainerRef.current) {
            previousScrollHeight.current = messagesContainerRef.current.scrollHeight;
          }
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(messagesTopRef.current);

    return () => {
      observer.disconnect();
    };
  }, [onLoadMore, hasMore, loadingMore]);

  useEffect(() => {
    setIsFavorited(conversation.isFavorited || false);
  }, [conversation.isFavorited]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleToggleFavorite = async () => {
    const newFavoriteStatus = !isFavorited;
    const success = await chatService.toggleFavorite(conversation.id, newFavoriteStatus);
    if (success) {
      setIsFavorited(newFavoriteStatus);
      onConversationUpdate?.();
    }
    setShowMenu(false);
  };

  const handleMarkAsUnread = async () => {
    await chatService.markAsUnread(conversation.id);
    onConversationUpdate?.();
    setShowMenu(false);
  };

  const getConversationName = () => {
    if (conversation.type === 'group' && conversation.name) {
      return conversation.name;
    }
    if (conversation.otherParticipant?.user?.name) {
      return conversation.otherParticipant.user.name;
    }
    return 'Usuário';
  };

  const getConversationAvatar = () => {
    if (conversation.otherParticipant?.user?.avatar) {
      return conversation.otherParticipant.user.avatar;
    }
    return null;
  };

  return (
    <S.Container>
      <S.Header>
        <S.HeaderContent>
          {onBackToList && (
            <S.BackButton onClick={onBackToList} title="Voltar para lista">
              <HiArrowLeft size={20} />
            </S.BackButton>
          )}
          <S.AvatarContainer>
            {getConversationAvatar() ? (
              <S.Avatar src={getConversationAvatar() || ''} alt="Avatar" />
            ) : (
              <S.AvatarPlaceholder>
                {getConversationName().charAt(0).toUpperCase()}
              </S.AvatarPlaceholder>
            )}
          </S.AvatarContainer>
          <S.HeaderInfo>
            <S.ConversationName>{getConversationName()}</S.ConversationName>
            {conversation.otherParticipant?.user && (
              conversation.otherParticipant.user.isOnline === true ? (
                <S.OnlineStatus>Online</S.OnlineStatus>
              ) : conversation.otherParticipant.user.isOnline === false ? (
                <S.OfflineStatus>Offline</S.OfflineStatus>
              ) : null
            )}
          </S.HeaderInfo>
        </S.HeaderContent>
        <S.HeaderRight>
          {conversation.type === 'group' && (
            <ButtonIcon
              onClick={() => setShowParticipantsModal(true)}
              color="transparent"
              widthSize="xxsmall"
              heightSize="small"
              icon={<HiUserGroup size={14} />}
            />
          )}
          <S.MenuWrapper ref={menuRef}>
            <ButtonIcon
              onClick={() => setShowMenu(!showMenu)}
              color="transparent"
              widthSize="xxsmall"
              heightSize="small"
              icon={<HiDotsVertical size={14} />}
            />
            {showMenu && (
              <S.Menu>
                <S.MenuItem onClick={handleToggleFavorite}>
                  {isFavorited ? (
                    <>
                      <HiStar />
                      <S.MenuItemText>Desfavoritar</S.MenuItemText>
                    </>
                  ) : (
                    <>
                      <HiOutlineStar />
                      <S.MenuItemText>Favoritar</S.MenuItemText>
                    </>
                  )}
                </S.MenuItem>
                <S.MenuItem onClick={handleMarkAsUnread}>
                  <HiMail />
                  <S.MenuItemText>Marcar como não lida</S.MenuItemText>
                </S.MenuItem>
              </S.Menu>
            )}
          </S.MenuWrapper>
        </S.HeaderRight>
      </S.Header>

      <S.MessagesContainer ref={messagesContainerRef}>
        {loadingMore && (
          <S.LoadingMore>
            <S.LoadingText>Carregando mais mensagens...</S.LoadingText>
          </S.LoadingMore>
        )}
        <div ref={messagesTopRef} />
        {messages.length === 0 ? (
          <S.EmptyMessages>Nenhuma mensagem ainda. Comece a conversar!</S.EmptyMessages>
        ) : (
          messages.map((message) => (
            <S.MessageWrapper key={message.id} isOwn={message.isOwn || false}>
              <S.Message isOwn={message.isOwn || false}>
                {!message.isOwn && message.sender && (
                  <S.SenderName>{message.sender.name}</S.SenderName>
                )}
                <S.MessageContent>{message.content}</S.MessageContent>
                <S.MessageTime>
                  {convertStringToDate(message.createdAt, true, 'date')}
                </S.MessageTime>
              </S.Message>
            </S.MessageWrapper>
          ))
        )}
        <div ref={messagesEndRef} />
      </S.MessagesContainer>

      <S.InputContainer>
        <MessageInput onSendMessage={onSendMessage} />
      </S.InputContainer>

      {conversation.type === 'group' && currentUser && (
        <GroupParticipantsModal
          isOpen={showParticipantsModal}
          onClose={() => setShowParticipantsModal(false)}
          conversationId={conversation.id}
          currentUserId={currentUser.id}
          users={users}
          onUpdate={onConversationUpdate}
        />
      )}
    </S.Container>
  );
}

